import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import {compose, graphql} from "react-apollo"
import UpsertDocumentBucket from '../documents/queries/UpsertDocumentBucket'
import LoanAdjustment from './queries/LoanAdjustment'
import SuccessNotification from '../common/SuccessNotification'
import Modal from "../common/Modal";
import styled from "styled-components";
import FadeInUpWrapper from "../common/FadeInUpWrapper";

const ActionLink = styled.a`
  cursor: pointer;
`
const ModalContainer = styled(FadeInUpWrapper)`
  width: 60%;
  background: white;
  box-shadow: 0 4px 10px -2px rgba(0,0,0,0.3);
  padding: 20px;
  text-align: center;
`
const Input = styled.input`
  margin: 10px 0;
`

const ErrorMessages = styled.div`
  color: #cc2626;
`

class ReferralFee extends React.Component {
  constructor(props) {
    const { documentId, documentStatus, referralAdjustment } = props
    const dollarAdjustment = (referralAdjustment * -1)
    super(props)
    this.state = {
      showAddDocument: this.getShowAddDocument(documentId),
      showEditFee: this.getShowEditFee(documentStatus),
      showEditInput: false,
      notificationText: '',
      isSuccessNotificationVisible: false,
      showEditFeeModal: false,
      errorMessage: '',
      dollarAdjustment,
      referralAdjustment
    }
  }

  static propTypes = {
    loanId: PropTypes.number.isRequired,
    documentId: PropTypes.number,
    referralAdjustment: PropTypes.number,
    documentStatus: PropTypes.string,
    superUser: PropTypes.bool,
    referralFeeOn: PropTypes.bool
  }

  getShowEditFee = status => {
    const { superUser, referralFeeOn } = this.props
    return referralFeeOn && superUser && status === 'approve_and_commit'
  }

  getShowAddDocument = documentId => {
    const { referralFeeOn } = this.props
    return referralFeeOn && documentId === null
  }

  onAddDocument = async event => {
    const { upsertDocumentBucket, loanId, documentId } = this.props
    await upsertDocumentBucket({
      variables: {
        input: {
          documentId, // this should be empty but it is required by the endpoint
          documentFields: {
            type: 'LoanFeeDiscountApproval',
            entityId: loanId,
            entityType: 'Loan'
          }
        }
      },
      update: (store, { data: { upsertDocumentBucket: { document } } }) => {
        this.handleDocumentSuccess(document, store)
      }
    })
  }

  handleDocumentSuccess = (documentData, store) => {
    const { rid, status } = documentData
    this.setState({
      showAddDocument: this.getShowAddDocument(rid),
      showEditFee: this.getShowEditFee(status),
    })
    this.flashSuccessNotification('Referral fee discount document bucket created, go to business documents section to upload it')
  }

  editShowEditInput = showEditInput => {
    this.setState({ showEditInput })
  }

  flashSuccessNotification = text => {
    this.setState({
      isSuccessNotificationVisible: true,
      notificationText: text
    })
    setTimeout(
      this.closeSuccessNotification,
      4000
    )
  }

  closeSuccessNotification = () => {
    this.setState({ isSuccessNotificationVisible: false })
  }

  showConfirmationPopup = () => {
    this.setState({ showEditFeeModal: true })
  }

  updateDollarAdjustment = event => {
    const dollarAdjustment = parseInt(event.target.value)
    this.setState({ dollarAdjustment })
  }

  closeFeeDiscountModal = () => {
    this.setState({
      showEditFeeModal: false,
      errorMessage: ''
    })
    this.editShowEditInput(false)
  }

  applyFeeDiscount = async () => {
    const { loanAdjustment, loanId } = this.props
    const { dollarAdjustment } = this.state
    await loanAdjustment({
      variables: {
        input: {
          loanId,
          dollarAdjustment,
          type: 'referral_fee'
        }
      },
      update: (store, { data: { loanAdjustment: { loanAdjustment } } }) => {
        this.handleLoanAdjustmentSuccess(loanAdjustment)
      }
    }).catch((errors) => {
      const errorMessage = errors.message.replace(/^[A-Z][A-Za-z]*\serror:\s/g, '')
      this.setState({ errorMessage })
    })
  }

  handleLoanAdjustmentSuccess = (loanAdjustment) => {
    const { dollarAdjustment } = loanAdjustment
    this.setState({ referralAdjustment: dollarAdjustment })
    this.flashSuccessNotification('Referral fee applied')
    this.closeFeeDiscountModal()
  }

  get renderSuccessNotification () {
    const { isSuccessNotificationVisible, notificationText } = this.state
    if (isSuccessNotificationVisible) {
      return (
        <SuccessNotification
          onClose={this.closeSuccessNotification}
          duration={3}
          text={notificationText}
        />
      )
    }
  }

  get renderConfirmationModal () {
    const { showEditFeeModal, dollarAdjustment, errorMessage } = this.state
    return showEditFeeModal && (
      <Modal light isModalOpen={showEditFeeModal} className="popups">
        <ModalContainer className="popup">
          <div className="content">
            <h3 className="title">
              Please review and confirm the discount amount:
            </h3>
            <p className="description">
              You will be applying a <strong>${dollarAdjustment}</strong> discount to this loan, do you wish to apply?
            </p>
            <button
              className="btn-gray"
              onClick={this.closeFeeDiscountModal}>
              Cancel
            </button>
            <button
              className="btn-default"
              onClick={this.applyFeeDiscount}>
              Apply
            </button>
            <ErrorMessages>{errorMessage}</ErrorMessages>
          </div>
        </ModalContainer>
      </Modal>
    )
  }

  get renderFeeInput () {
    const { dollarAdjustment } = this.state
    return (
      <Fragment>
        <Input
          id="dollarAdjustment"
          name="dollarAdjustment"
          type="number"
          value={dollarAdjustment}
          onChange={this.updateDollarAdjustment}
        />
        <button
          className="btn btn-gray"
          onClick={() => this.editShowEditInput(false)}>
          <i className="icon icon-remove" />
        </button>
        <button
          className="btn btn-warning"
          onClick={this.showConfirmationPopup}>
          <i className="icon icon-ok" />
        </button>
      </Fragment>
    )
  }

  render() {
    const { showEditFee, showAddDocument, showEditInput, referralAdjustment } = this.state
    return (
      <Fragment>
        <td>Referral Fee Discount</td>
        <td>${showEditInput ? this.renderFeeInput : referralAdjustment}</td>
        <td>
          {showEditFee && !showEditInput && (
            <ActionLink onClick={() => this.editShowEditInput(true)}>
              Edit
            </ActionLink>
          )}
          {showAddDocument && (
            <ActionLink onClick={this.onAddDocument}>
              Add
            </ActionLink>
          )}
          { this.renderConfirmationModal }
          { this.renderSuccessNotification }
        </td>
      </Fragment>
    )
  }
}

export { ReferralFee }

export default compose(
  graphql(UpsertDocumentBucket, {
    name: 'upsertDocumentBucket'
  }),
  graphql(LoanAdjustment, {
    name: 'loanAdjustment'
  })
)(ReferralFee)
